import React, { useEffect, useRef, useState } from 'react';
import { Siren, Car, User, Wrench } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const KEY = 'sm-dismissed-pscases';
const getDismissed = () => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; } };
const addDismissed = (id) => { const d = getDismissed().slice(-150); d.push(id); localStorage.setItem(KEY, JSON.stringify(d)); };

const ALERT_ROLES = ['Service Manager', 'Service Advisor', 'Service Inspector', 'Post-Sale Manager', 'Owner', 'Administrator'];
// Sirene apenas para quem está na oficina. Gerência, Owner e Admin só veem o alerta na tela.
const SIREN_ROLES = ['Service Manager', 'Service Advisor', 'Service Inspector'];

const playSiren = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const g = ctx.createGain(); g.connect(ctx.destination);
    g.gain.setValueAtTime(0.9, ctx.currentTime);
    [['sawtooth', 600, 1050], ['square', 1200, 1900]].forEach(([type, lo, hi]) => {
      const o = ctx.createOscillator();
      o.type = type; o.connect(g);
      o.frequency.setValueAtTime(lo, ctx.currentTime);
      for (let i = 0; i < 10; i++) {
        o.frequency.linearRampToValueAtTime(hi, ctx.currentTime + i * 0.6 + 0.3);
        o.frequency.linearRampToValueAtTime(lo, ctx.currentTime + i * 0.6 + 0.6);
      }
      o.start(); o.stop(ctx.currentTime + 6);
    });
    g.gain.setValueAtTime(0.9, ctx.currentTime + 5.6);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 6);
  } catch (e) {}
};

export const PostSaleAlert = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const soundedRef = useRef(new Set());

  useEffect(() => {
    if (!user || !ALERT_ROLES.includes(user.role)) return;
    let alive = true;
    const check = async () => {
      try {
        const { data } = await api.get('/post_sale_cases');
        const dismissed = getDismissed();
        const cutoff = Date.now() - 24 * 3600 * 1000;
        const fresh = data.filter((c) =>
          c.status === 'New' && c.created_at && new Date(c.created_at).getTime() > cutoff &&
          !dismissed.includes(c.id) && c.createdBy !== user.name);
        if (!alive) return;
        setAlerts(fresh);
        const unsounded = fresh.filter((c) => !soundedRef.current.has(c.id));
        if (unsounded.length && SIREN_ROLES.includes(user.role)) playSiren();
        unsounded.forEach((c) => soundedRef.current.add(c.id));
      } catch (e) {}
    };
    check();
    const iv = setInterval(check, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [user]);

  if (alerts.length === 0) return null;
  const c = alerts[0];
  const dismiss = () => { addDismissed(c.id); setAlerts((prev) => prev.filter((x) => x.id !== c.id)); };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="post-sale-alert-overlay">
      <div className="absolute inset-0 bg-black/90" />
      <div className="relative z-10 w-full max-w-lg rounded-[3px] border border-[hsl(var(--bad))] bg-card">
        <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-border" style={{ borderTop: '2px solid hsl(var(--bad))' }}>
          <Siren className="w-4 h-4 t-bad animate-bell-shake" />
          <span className="ent-coltitle uppercase tracking-wide t-bad">Pós-Venda · Novo caso recebido</span>
          <span className="ent-pill ml-auto">{alerts.length}</span>
        </div>
        <div className="p-4 space-y-3">
          <h2 className="font-display font-bold text-xl leading-tight" data-testid="ps-alert-title">{c.cliente}</h2>
          <div className="ent-ribbon">
            <div><p className="ent-label">Cliente</p><p className="text-[13px] font-semibold mt-1 truncate" data-testid="ps-alert-cliente">{c.cliente}</p></div>
            <div><p className="ent-label">Tipo</p><p className="text-[13px] font-semibold mt-1">{c.tipo}</p></div>
            <div><p className="ent-label">Veículo</p><p className="text-[13px] font-semibold mt-1 truncate">{c.veiculo || '—'}</p></div>
          </div>
          {c.reclamacao && <p className="text-[12.5px] text-foreground/80 border-l-2 border-[hsl(var(--bad))] pl-2.5">{c.reclamacao}</p>}
          <p className="text-[11px] t-label">Aberto por {c.createdBy}{c.agendado ? ` · Serviço agendado ${c.appointment_date} ${c.appointment_time}` : ''}</p>
          {alerts.length > 1 && <p className="text-[11px] t-bad">+ {alerts.length - 1} outro(s) caso(s) aguardando</p>}
          <button data-testid="ps-alert-dismiss" onClick={dismiss} className="ent-btn ent-btn-primary w-full justify-center py-3 text-[13px]">
            OK, recebido
          </button>
        </div>
      </div>
    </div>
  );
};
